import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaArrowRight,
  FaCheckCircle,
  FaExternalLinkAlt,
  FaLock,
  FaRobot,
} from 'react-icons/fa';
import NavigationBar from './components/NavigationBar';
import { AuthContext, axiosInstance } from './AuthContext';

const ALPACA_ACCOUNT_URL = 'https://app.alpaca.markets/';

const OnboardingPage = () => {
  const { token, isTokenVerified } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [accountPath, setAccountPath] = useState('existing');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [statusError, setStatusError] = useState(false);
  const [statusRefresh, setStatusRefresh] = useState(0);
  const connectInFlight = useRef(false);
  const [oauthStatus, setOauthStatus] = useState({ configured: false, connected: false, loading: true });

  useEffect(() => {
    if (!token || !isTokenVerified) return;
    axiosInstance.get('/api/alpaca/oauth/status')
      .then(({ data }) => { setOauthStatus({ ...data, loading: false }); setStatusError(false); })
      .catch(() => { setOauthStatus({ configured: false, connected: false, loading: false }); setStatusError(true); });
  }, [token, isTokenVerified, statusRefresh]);

  useEffect(() => {
    const result = searchParams.get('alpaca');
    if (result === 'connected') {
      setOauthStatus((current) => ({ ...current, connected: true }));
    } else if (result === 'cancelled') {
      setError('Alpaca connection was cancelled. Nothing was connected and no order was created. You can try again.');
    } else if (result === 'failed') {
      setError('Alpaca could not complete the market-data connection. Nothing was connected. Please try again.');
    }
  }, [searchParams]);

  if (!token || !isTokenVerified) return null;

  const startAlpacaConnect = async () => {
    if (connectInFlight.current) return;
    connectInFlight.current = true;
    setBusy(true);
    setError('');
    try {
      const { data } = await axiosInstance.post('/api/alpaca/oauth/start');
      window.location.assign(data.authorization_url);
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : detail?.message || 'Unable to start the Alpaca market-data connection.',
      );
      setBusy(false);
      connectInFlight.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <NavigationBar />
      <main className="mx-auto max-w-4xl px-4 pb-16 pt-24 sm:px-6 lg:px-8">
        <section className="rounded-3xl bg-slate-950 px-6 py-8 text-white shadow-xl sm:px-10">
          <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-cyan-300"><FaRobot /> Meet your first bot</p>
          <h1 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">Connect market data, then build your bot.</h1>
          <p className="mt-4 max-w-3xl text-base leading-7 text-slate-300 sm:text-lg">Your bot periodically checks completed daily market evidence and applies the strategy you choose. Matching conditions become Practice suggestions in Today; they never become transactions without your approval.</p>
        </section>

        <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-8">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-100 text-xl text-blue-700 dark:bg-blue-950 dark:text-blue-300"><FaLock /></span>
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">Required market-data step</p>
              <h2 className="mt-1 text-2xl font-black">Connect Alpaca Paper through OAuth</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">Alpaca supplies the market data your bot needs. Pinngro Practice remains an internal virtual environment—it does not submit Alpaca Paper or Live orders.</p>
            </div>
          </div>

          {oauthStatus.loading ? (
            <p role="status" className="mt-6 rounded-2xl bg-slate-100 p-5 text-sm font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">Checking your Alpaca Paper market-data connection…</p>
          ) : statusError ? (
            <div role="alert" className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
              <p className="font-bold">We could not check your market-data connection.</p>
              <p className="mt-1">Your connection status has not been changed. Try the check again before connecting a bot.</p>
              <button type="button" onClick={() => { setOauthStatus((current) => ({ ...current, loading: true })); setStatusRefresh((current) => current + 1); }} className="mt-3 rounded-xl bg-amber-800 px-4 py-2.5 font-bold text-white hover:bg-amber-900">Retry status check</button>
            </div>
          ) : oauthStatus.connected && oauthStatus.verified ? (
            <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900 dark:bg-emerald-950/30 sm:flex-row sm:items-center">
              <FaCheckCircle className="shrink-0 text-2xl text-emerald-600" />
              <div className="flex-1">
                <p className="font-black text-emerald-900 dark:text-emerald-200">Market data connected</p>
                <p className="mt-1 text-sm text-emerald-800 dark:text-emerald-300">Your Alpaca Paper OAuth connection is ready. No broker order was created.</p>
              </div>
              <button type="button" onClick={() => navigate('/bots?welcome=connected&step=1#hire', { replace: true })} className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white hover:bg-emerald-700">Create your first bot <FaArrowRight /></button>
            </div>
          ) : (
            <>
              {oauthStatus.connected && !oauthStatus.verified && (
                <p role="alert" className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm font-semibold text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">Your saved Alpaca authorization could not be verified. Reconnect below before creating your first bot.</p>
              )}
              <div className="mt-6" role="radiogroup" aria-label="Alpaca account setup">
                <p className="text-sm font-bold">Which describes you?</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <button type="button" role="radio" aria-checked={accountPath === 'existing'} onClick={() => setAccountPath('existing')} className={`rounded-2xl border-2 p-4 text-left transition ${accountPath === 'existing' ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-100 dark:bg-blue-950/30 dark:ring-blue-950' : 'border-slate-200 dark:border-slate-700'}`}>
                    <span className="font-black">I already have Alpaca</span>
                    <span className="mt-1 block text-sm text-slate-600 dark:text-slate-300">Connect your Paper account and return to Pinngro.</span>
                  </button>
                  <button type="button" role="radio" aria-checked={accountPath === 'new'} onClick={() => setAccountPath('new')} className={`rounded-2xl border-2 p-4 text-left transition ${accountPath === 'new' ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-100 dark:bg-blue-950/30 dark:ring-blue-950' : 'border-slate-200 dark:border-slate-700'}`}>
                    <span className="font-black">I need an Alpaca account</span>
                    <span className="mt-1 block text-sm text-slate-600 dark:text-slate-300">Create a Paper-capable account first; no deposit is needed for Pinngro Practice.</span>
                  </button>
                </div>
              </div>

              {accountPath === 'new' && (
                <div className="mt-5 rounded-2xl border border-cyan-200 bg-cyan-50 p-5 text-sm leading-6 text-cyan-950 dark:border-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-100">
                  <p className="font-black">Create your Alpaca Paper-capable account</p>
                  <ol className="mt-2 list-decimal space-y-1 pl-5">
                    <li>Open Alpaca and complete Alpaca’s own signup and identity process.</li>
                    <li>Use its Paper environment. You do not need to deposit or fund an account to use Pinngro Practice.</li>
                    <li>Return to this page and choose “Connect Alpaca for market data.”</li>
                  </ol>
                  <a href={ALPACA_ACCOUNT_URL} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-xl border border-cyan-500 px-4 py-2.5 font-bold">Open Alpaca account setup <FaExternalLinkAlt /></a>
                </div>
              )}

              <div className="mt-5 rounded-2xl border border-blue-200 bg-blue-50 p-5 text-sm leading-6 text-blue-950 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100">
                <p className="font-black">What you are authorizing in this Human Beta</p>
                <ul className="mt-2 space-y-1">
                  <li>• Pinngro uses your Alpaca Paper OAuth connection for market data.</li>
                  <li>• You enter your credentials on Alpaca; Pinngro never receives your Alpaca password or asks you to paste API keys.</li>
                  <li>• Eligible customers can separately review and submit manual Alpaca Paper orders in Portfolio. Pinngro does not submit Live or bot-driven broker orders.</li>
                  <li>• You do not need to complete or fund an Alpaca Live trading account for Pinngro.</li>
                  <li>• Approving a Pinngro Practice suggestion creates only an internal virtual action—not an Alpaca order.</li>
                </ul>
                <p className="mt-3 text-xs text-blue-800 dark:text-blue-200">Alpaca controls the wording on its authorization page. Pinngro checks account permissions and manual Paper eligibility before any order review or submission.</p>
              </div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">OAuth can be revoked later. Connecting does not start a bot or create a transaction.</p>
                <button type="button" onClick={startAlpacaConnect} disabled={busy || !oauthStatus.configured} aria-busy={busy} className="shrink-0 rounded-xl bg-blue-600 px-5 py-3 font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300 dark:disabled:bg-slate-700">{busy ? 'Opening Alpaca…' : oauthStatus.configured ? 'Connect Alpaca for market data' : 'Connection temporarily unavailable'}</button>
              </div>
              {busy && <p role="status" className="mt-2 text-sm font-semibold text-blue-800 dark:text-blue-200">Starting your market-data connection…</p>}
              {error && <p role="alert" className="mt-2 rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-700 dark:bg-red-950/40 dark:text-red-300">{error}</p>}
              {!oauthStatus.configured && <p className="mt-3 text-xs font-semibold text-amber-700 dark:text-amber-300">Alpaca OAuth is temporarily unavailable. Bot market watching cannot be started until this required connection is available.</p>}
            </>
          )}

        </section>

        <p className="mt-6 text-center text-xs leading-5 text-slate-500">Pinngro is software, not a broker. Alpaca remains responsible for its account setup and authorization experience. <Link to="/privacy" className="text-blue-600 hover:underline">How we protect your data</Link></p>
      </main>
    </div>
  );
};

export default OnboardingPage;
